import type {
	SRT_RowSelectionState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type { PricingCampaignModel, ScenarioModel } from '@/shared/generated'
import type { OnChangeFn } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import type { PricingCampaignTableRow } from '../model/types'

import React, { useMemo, useState } from 'react'
import { isNil } from 'lodash/fp'
import { CircleDashedIcon, TextIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	EngineBadge,
	GlobalPcBadge,
	isGlobalPricingCampaign,
} from '@/entities/pricing-campaign'

import {
	createSRTColumnHelper,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { Badge } from '@/shared/components/ui/badge'
import { LinkButton } from '@/shared/components/ui/link-button'
import { PricingEngineType } from '@/shared/generated'
import { getMasterTableLocale } from '@/shared/i18n'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

const isDisabledInteraction = (campaign: PricingCampaignModel) =>
	isGlobalPricingCampaign(campaign)

export const useScenarioPricingCampaignsTable = ({
	scenario,
	rowSelection,
	setRowSelection,
	data,
	isLoading,
}: {
	scenario?: ScenarioModel
	rowSelection: SRT_RowSelectionState
	setRowSelection: OnChangeFn<SRT_RowSelectionState>
	data: PricingCampaignTableRow[]
	isLoading?: boolean
}) => {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SRT_SortingState>([])
	const columns = useMemo(() => getColumns(t, scenario), [scenario, t])

	return useMasterReactTable<PricingCampaignTableRow>({
		localization: getMasterTableLocale(),
		data,
		columns,
		state: {
			isLoading,
			sorting,
			rowSelection,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		getRowId: (row) => row?.campaign?.id,
		layoutMode: 'grid',
		enableVirtualization: true,
		enableColumnActions: false,
		enableColumnFilters: true,
		divideX: false,
		onRowSelectionChange: setRowSelection,
		enableRowSelection: (row) => !isDisabledInteraction(row.original.campaign),
		onSortingChange: setSorting,
		getRowHeight: () => 52,
		getRowClassName: ({ row }) =>
			row?.original.campaign && !row.getCanSelect()
				? 'opacity-60 h-13'
				: 'h-13',
	})
}

const columnHelper = createSRTColumnHelper<PricingCampaignTableRow>()
const getColumns = (t: TFunction, scenario?: ScenarioModel) => [
	columnHelper.accessor('campaign.name', {
		header: t('pc_name'),
		meta: {
			label: t('pc_name'),
			placeholder: t('search.pc.placeholder', {
				defaultValue: 'Search pricing campaigns...',
			}),
			variant: 'text',
			icon: TextIcon,
		},
		enableColumnFilter: true,
		Cell: ({ cellValue, row }) => {
			const { campaign } = row.original
			const { showModal } = useModals()

			return (
				<div className='flex items-center space-x-2'>
					{isGlobalPricingCampaign(campaign) ? (
						<span className='block cursor-not-allowed text-primary'>
							{cellValue}
						</span>
					) : (
						<LinkButton
							variant='brand'
							className='text-left'
							onClick={() => {
								ampli.settingsPricingCampaignsViewClick({
									location: 'scenario-pricing-campaigns-table',
								})
								showModal('PRICING_CAMPAIGN_MODAL', {
									// If the scenario is main, we can't edit the pricing campaign
									isReadOnly: scenario?.is_main,
									pricingCampaignId: campaign.id,
									optimizationGroupId: campaign.optimization_group_id,
									scenarioId: campaign.scenario_id,
								})
							}}
						>
							{cellValue}
						</LinkButton>
					)}
				</div>
			)
		},
	}),
	columnHelper.accessor('campaign.order', {
		size: 70,
		header: t('general_priority'),
		Cell: ({ cellValue }) => {
			const value = cellValue

			return value ? <Badge>{value}</Badge> : '-'
		},
	}),
	columnHelper.accessor('campaign.settings.pricing_tactics.engine', {
		size: 170,
		header: t('pricing_engine'),
		meta: {
			label: t('pricing_engine'),
			variant: 'multi-select',
			options: [
				{
					label: t(PricingEngineType.MD, { defaultValue: 'Markdown' }),
					value: PricingEngineType.MD,
				},
				{
					label: t(PricingEngineType.ML, { defaultValue: 'Demand-based' }),
					value: PricingEngineType.ML,
				},
				{
					label: t(PricingEngineType.RB, { defaultValue: 'Rule-based' }),
					value: PricingEngineType.RB,
				},
				{
					label: t(PricingEngineType.Promo, { defaultValue: 'Promo' }),
					value: PricingEngineType.Promo,
				},
			],
			icon: CircleDashedIcon,
		},
		Cell: ({ row, cellValue }) => {
			const { t } = useTranslation()

			const { campaign } = row.original
			const engine = cellValue

			return (
				<div className='flex items-center space-x-1'>
					<EngineBadge engine={engine} className='mr-1'>
						{t(engine, { defaultValue: engine })}
					</EngineBadge>

					{isGlobalPricingCampaign(campaign) && <GlobalPcBadge />}
				</div>
			)
		},
	}),
	columnHelper.accessor('products_count', {
		size: 120,
		header: t('items_in_pc'),
		Cell: ({ cellValue, row }) => {
			const value = cellValue
			const { assortment_share } = row.original
			if (isNil(value)) {
				return '-'
			}

			return (
				<div className='font-mono'>{`${formatNumber(value)} (${formatNumber(assortment_share || 0, NumberFormats.Percent)})`}</div>
			)
		},
	}),
]
